import React, { useEffect, useRef, useState } from "react";
import SubLayout from "../../components/layout/SubLayout";
import {
  Button,
  Col,
  Drawer,
  Input,
  Row,
  Spin,
  message,
  Checkbox,
  Modal,
} from "antd";
import ProfileCards from "../../components/common/search/profileCards";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SearchListItem from "../../components/common/search/SearchListItem";
import API_MANAGER from "../../API";
import CustomPagination from "../../components/common/CustomPagination";
import FullScreenLoader from "../../components/common/FullScreenLoader";
import FIlterIcon from "../../Assets/filterIcon.svg";
import CloseIcon from "../../Assets/cancelIcon.svg";
import CancelIcon from "../../Assets/cancelIcon.svg";
import InviteCampaignDropdown from "../../components/common/search/InviteCampaignDropdown";

const FILTERS_STORAGE_KEY = "globalSearchFilters";
const FILTERS_EXPIRATION_MINUTES = 1;

// Helper function to check if filters are expired
const areFiltersExpired = (timestamp) => {
  if (!timestamp) return true;
  const expirationTime = FILTERS_EXPIRATION_MINUTES * 60 * 1000;
  const currentTime = Date.now();
  return (currentTime - timestamp) > expirationTime;
};

// Helper function to save filters with timestamp
const saveFiltersWithTimestamp = (filtersToSave) => {
  try {
    const filterData = {
      filters: filtersToSave,
      timestamp: Date.now(),
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filterData));
  } catch (error) {
    console.error("Failed to save filters to localStorage:", error);
  }
};

// Helper function to get filters from localStorage (returns null if expired)
const getFiltersFromStorage = () => {
  try {
    const storedData = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!storedData) return null;
    
    const parsedData = JSON.parse(storedData);
    
    if (parsedData.timestamp && parsedData.filters) {
      if (areFiltersExpired(parsedData.timestamp)) {
        localStorage.removeItem(FILTERS_STORAGE_KEY);
        return null;
      }
      return parsedData.filters;
    }
    
    if (parsedData.filters) {
      return parsedData.filters;
    }
    
    return parsedData;
  } catch (error) {
    console.error("Failed to get filters from localStorage:", error);
    return null;
  }
};

function Search() {
  const [searchParams] = useSearchParams();
  const [searchedData, setSearchedData] = useState();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [campaignList, setCampaignList] = useState([]);
  const [bulkCampaignList, setBulkCampaignList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState(null);
  const [filterLength, setFilterLength] = useState(0);
  const [count, setCount] = useState(0);
  const [favourites, setFavourites] = useState([]);
  const [loadingFavourites, setLoadingFavourites] = useState(false);
  const [isFavouritesTab, setIsFavouritesTab] = useState(false);
  const inputRef = useRef(null);

  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [isBulkInviteModalOpen, setIsBulkInviteModalOpen] = useState(false);
  const [bulkInviteLoading, setBulkInviteLoading] = useState(false);
  const [bulkInviteIds, setBulkInviteIds] = useState({});

  const navigate = useNavigate();
  const { state } = useLocation();
  const filterData = [
    { id: 1, name: "Location", value: "STATE", label: "location" },
    { id: 2, name: "Job Title", value: "JOBTITLE", label: "jobTitle" },
    { id: 3, name: "Industry", value: "INDUSTRY", label: "industry" },
    { id: 4, name: "Skills", value: "SKILL", label: "skills" },
    {
      id: 5,
      name: "Qualifications",
      value: "QUALIFICATION",
      label: "qualifications",
    },
    { id: 6, name: "Inductions", value: "INDUCTION", label: "inductions" },
    { id: 7, name: "Medicals", value: "MEDICAL_ASSESSMENT", label: "medicals" },
    { id: 8, name: "Ethnicity", value: "ETHNICITY", label: "ethnicity" },
    { id: 9, name: "Gender", value: "GENDER", label: "gender" },
    {
      id: 10,
      name: "Citizenship",
      value: "CITIZENSHIP",
      label: "citizenship",
      options: [
        {
          label: "Australian",
          value: "YES",
        },
        {
          label: "Non-Australian",
          value: "NO",
        },
      ],
    },
    { id: 11, name: "Visa", value: "VISA", label: "visa" },
  ];
  useEffect(() => {
    const element = document.getElementById("scrolling");
    element.scrollTo(0, 0);
  }, [page]);

  // Get favourites list
  const getFavourites = async () => {
    setLoadingFavourites(true);
    try {
      const response = await API_MANAGER.getAllFavourites();
      const favouritesList = response?.data?.data || [];
      setFavourites(favouritesList);
    } catch (error) {
      console.error("Error fetching favourites:", error);
    } finally {
      setLoadingFavourites(false);
    }
  };

  // Check if a candidate is in favourites
  const isInFavourites = (candidateId) => {
    return favourites.some(
      (fav) =>
        fav.candidateId?._id === candidateId ||
        fav.candidateId?.id === candidateId ||
        fav.candidateId === candidateId
    );
  };

  // Handle add/remove from favourites
  const handleFavouriteToggle = async (candidateId, candidateName) => {
    try {
      if (isInFavourites(candidateId)) {
        // Find the favourite entry to remove
        const favouriteEntry = favourites.find(
          (fav) =>
            fav.candidateId?._id === candidateId ||
            fav.candidateId?.id === candidateId ||
            fav.candidateId === candidateId
        );
        if (favouriteEntry) {
          await API_MANAGER.removeFavourite(favouriteEntry._id);
        }
      } else {
        // Add to favourites
        await API_MANAGER.addFavourite({
          candidateId: candidateId,
          candidateName: candidateName,
        });
      }
      // Refresh favourites list
      getFavourites();
    } catch (error) {
      console.error("Error toggling favourite:", error);
    }
  };

  const GetGlobalSearch = async () => {
    setLoading(true);
   try {
      // Filter out internal location_locations key - only send location with formatted values
      const apiFilters = { ...filters };
      delete apiFilters.location_locations;
      
      const params = {
        ...apiFilters,
        search: search,
        limit: 10,
        page: page,
      };
      const response = await API_MANAGER.getGlobalDetailSearch(params);
      setSearchedData(response?.data?.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      message.error("Something went wrong. Please try again.");
    }
  };
  const singleUserCampaignDetails = async () => {
    const param = {
      search: campaignSearch ? campaignSearch : "",
      sort: "-createdAt",
      limit: 20000,
      page: 1,
    };
    try {
      const response = await API_MANAGER.getSingleUserCampaignDetails(
        param,
        localStorage.getItem("userID")
      );
      setCampaignList(response?.data?.data);
    } catch (error) {
      message.error("Something went wrong. Please try again.");
    }
  };
  useEffect(() => {
    singleUserCampaignDetails();
  }, [campaignSearch]);
  useEffect(() => {
    if (searchParams) {
      const temp = searchParams.get("search");
      const normalizedSearch = (temp || "").replace("?", "").split("%20").join(" ");
      setSearch(normalizedSearch);

      // Check if we're on favourites tab
      const tab = searchParams.get("tab");
      if (tab === "favourites") {
        setIsFavouritesTab(true);
        getFavourites();
      } else {
        setIsFavouritesTab(false);
      }
    }
    if (searchParams.get("page")) {
      setPage(parseInt(searchParams.get("page")));
    }
  }, [
    searchParams.get("search"),
    searchParams.get("page"),
    searchParams.get("tab"),
  ]);
  const handlePageChange = (pageNumber) => {
    const encodedSearch = (search || "").split(" ").join("%20");
    const link = `/search?search=${encodedSearch}&page=${pageNumber}${
      isFavouritesTab ? "&tab=favourites" : ""
    }`;
    navigate(link);
  };
  useEffect(() => {
    setFilterLength(0);
    let temp = 0;
    Object.entries(filters).map(([key, value]) => {
      if (value?.length > 1) {
        temp = temp + 1;
      }
    });
    setFilterLength(temp);
    if (search && search?.length > 0) {
      GetGlobalSearch();
    }
  }, [search, page, filters]);
  // Track if we've already restored state to prevent infinite loops
  const hasRestoredStateRef = useRef(false);
  const hasRestoredFromStorageRef = useRef(false);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (hasRestoredFromStorageRef.current) {
      saveFiltersWithTimestamp(filters);
    }
  }, [filters]);
  
  // Restore filters from localStorage on mount (if not in location.state)
  useEffect(() => {
    if (!hasRestoredFromStorageRef.current) {
      try {
        const storedFilters = getFiltersFromStorage();
        if (storedFilters) {
          // Only restore if location.state doesn't have filters (prioritize location.state)
          // Check both state and if we've already restored from state
          if ((!state?.filter || Object.keys(state.filter).length === 0) && !hasRestoredStateRef.current) {
            if (Object.keys(storedFilters).length > 0) {
              setFilters(storedFilters);
            }
          }
        }
        hasRestoredFromStorageRef.current = true;
      } catch (error) {
        console.error("Failed to restore filters from localStorage:", error);
        hasRestoredFromStorageRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Restore filters, search, and page from location.state when navigating back
  useEffect(() => {
    if (state && !hasRestoredStateRef.current) {
      let shouldRestore = false;
      
      // Restore filters (prioritize location.state over localStorage)
      if (state.filter && Object.keys(state.filter).length > 0) {
        setFilters(state.filter);
        // Also save to localStorage for future use with timestamp
        saveFiltersWithTimestamp(state.filter);
        shouldRestore = true;
      }
      
      // Restore search text
      if (state.searchText !== undefined && state.searchText !== search) {
        setSearch(state.searchText);
        shouldRestore = true;
      }
      
      // Restore favourites tab state if applicable
      if (state.tab === "favourites" && !isFavouritesTab) {
        setIsFavouritesTab(true);
        getFavourites();
        shouldRestore = true;
      }
      
      // Restore page (after other states are set)
      if (state.page && state.page !== page) {
        setPage(state.page);
        // Update URL to reflect the restored page
        const encodedSearch = (state.searchText || search || "").split(" ").join("%20");
        const link = `/search?search=${encodedSearch}&page=${state.page}${
          state.tab === "favourites" ? "&tab=favourites" : ""
        }`;
        navigate(link, { replace: true });
        shouldRestore = true;
      }
      
      // Mark as restored if we did any restoration
      if (shouldRestore) {
        hasRestoredStateRef.current = true;
      }
    }
    
    // Reset the ref when state changes (new navigation)
    if (!state) {
      hasRestoredStateRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  useEffect(() => {
    inputRef?.current?.focus({
      cursor: "end",
    });
  }, []);

  const getBulkCampaignList = async () => {
    try {
      const response = await API_MANAGER.getBulkCampaignList();
      setBulkCampaignList(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching bulk campaign list:", error);
      message.error("Failed to load campaign list. Please try again.");
    }
  };

  useEffect(() => {
    getBulkCampaignList();
  }, []);

  // Filter favourites based on search text
  const getFilteredFavourites = () => {
    if (!search || search.trim() === "") {
      return favourites;
    }
    return favourites.filter((favourite) => {
      const candidate = favourite.candidateId;
      const searchLower = search.toLowerCase();
      return (
        candidate?.name?.toLowerCase().includes(searchLower) ||
        candidate?.email?.toLowerCase().includes(searchLower) ||
        candidate?.discipline?.name?.toLowerCase().includes(searchLower) ||
        candidate?.city?.toLowerCase().includes(searchLower) ||
        candidate?.state?.toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredFavourites = getFilteredFavourites();
  const ITEMS_PER_PAGE = 10;
  const favouritesStartIndex = (page - 1) * ITEMS_PER_PAGE;
  const favouritesEndIndex = favouritesStartIndex + ITEMS_PER_PAGE;
  const pagedFavourites = filteredFavourites.slice(
    favouritesStartIndex,
    favouritesEndIndex
  );
  const displayData = isFavouritesTab
    ? pagedFavourites
    : searchedData?.result || [];
  const totalCount = isFavouritesTab
    ? filteredFavourites.length
    : searchedData?.totalCount || 0;

  // Helper to get a consistent ID from different candidate object structures
  const getCandidateId = (candidate) => {
    if (!candidate) return null;
    if (candidate._id && candidate._id) return candidate._id;
    if (candidate.userId) return candidate.userId;
    return candidate._id;
  };

  // Handler for individual candidate selection
  const handleCandidateSelectToggle = (candidateId) => {
    setSelectedCandidates((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(candidateId)) {
        newSelected.delete(candidateId);
      } else {
        newSelected.add(candidateId);
      }
      return Array.from(newSelected);
    });
  };

  const displayDataIds = displayData
    .map((d) => getCandidateId(isFavouritesTab ? d?.candidateId : d))
    .filter((id) => id);

  // "Select All" toggle
  const handleSelectAllToggle = (e) => {
    if (e.target.checked) {
      setSelectedCandidates((prev) => {
        // merge current page IDs with previously selected ones
        const merged = new Set([...prev, ...displayDataIds]);
        return Array.from(merged);
      });
    } else {
      setSelectedCandidates((prev) =>
        // remove only current page's IDs, keep the rest
        prev.filter((id) => !displayDataIds.includes(id))
      );
    }
  };

  // Check if all items on current page are selected
  const isAllCurrentPageSelected = displayDataIds.length > 0 && 
    displayDataIds.every(id => selectedCandidates.includes(id));

  // Check if some (but not all) items on current page are selected
  const isSomeCurrentPageSelected = displayDataIds.some(id => selectedCandidates.includes(id)) && 
    !isAllCurrentPageSelected;

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
    try {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear filters from localStorage:", error);
    }
    // Update URL to reflect cleared filters
    const encodedSearch = (search || "").split(" ").join("%20");
    const link = `/search?search=${encodedSearch}&page=1${
      isFavouritesTab ? "&tab=favourites" : ""
    }`;
    navigate(link);
  };

  // Check and clean expired filters on component mount
  useEffect(() => {
    try {
      const storedData = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData.timestamp && areFiltersExpired(parsedData.timestamp)) {
          localStorage.removeItem(FILTERS_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error("Failed to check expired filters:", error);
    }
  }, []);

  // Logic to handle the bulk invitation submission
  const handleBulkInvite = async () => {
    setBulkInviteLoading(true);
    let adverts = [];
    Object.entries(bulkInviteIds).forEach(([campaignId, advertIds]) => {
      advertIds.forEach((advertId) => {
        adverts.push({ advertId, campaignId });
      });
    });

    if (adverts.length === 0) {
      message.error(
        "Please select at least one advert to invite candidates to."
      );
      setBulkInviteLoading(false);
      return;
    }

    const payload = { adverts, profileId: selectedCandidates };
    // console.log(payload,"TESTPAYLOAD")
    try {
      const response = await API_MANAGER.bulkInviteUsers(payload);
      if (response?.data?.isSuccess) {
        message.success("Candidate(s) successfully invited.");
        setIsBulkInviteModalOpen(false);
        setBulkInviteIds({});
        setSelectedCandidates([]);
      } else {
        message.error("Failed to invite candidates. Please try again.");
      }
    } catch (error) {
      console.error("Error inviting candidates:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setBulkInviteLoading(false);
    }
  };

  return (
    <div className="w-100">
      <SubLayout
        padding={true}
        page={isFavouritesTab ? "Favourites" : "Search Results"}
        showSearch={isFavouritesTab}
      >
        <Row className="search-main-container">
          <Col xs={24} md={isFavouritesTab ? 24 : 17} className="main-container-part1">
            <Row align={"middle"} justify={"space-between"} gutter={[16, 16]}>
              <Col span={isFavouritesTab ? 24 : 18}>
                <Input
                  id="searchBox"
                  className="search-input"
                  style={{ height: 36 }}
                  size="large"
                  value={search || ""}
                  onChange={(e) => {
                    setSearch(e?.target?.value);
                    const link = `/search?search=${e?.target?.value
                      ?.split(" ")
                      ?.join("%20")}&page=1${
                      isFavouritesTab ? "&tab=favourites" : ""
                    }`;
                    navigate(link);
                  }}
                  placeholder="Search name, email or discipline"
                  ref={inputRef}
                  
                  suffix={
                    search && (
                      <img
                        src={CancelIcon}
                        className="cursor_pointer"
                        alt="cross"
                        onClick={() => {
                          setSearch("");
                          const link = `/search?search=&page=1${
                            isFavouritesTab ? "&tab=favourites" : ""
                          }`;
                          navigate(link);
                        }}
                      />
                    )
                  }
                  prefix={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                    >
                      <path
                        d="M19.8947 19.0002L15.5517 14.6572M15.5517 14.6572C16.2946 13.9143 16.8839 13.0324 17.286 12.0618C17.688 11.0911 17.8949 10.0508 17.8949 9.00021C17.8949 7.9496 17.688 6.90929 17.286 5.93866C16.8839 4.96803 16.2946 4.08609 15.5517 3.34321C14.8088 2.60032 13.9269 2.01103 12.9563 1.60898C11.9857 1.20693 10.9453 1 9.89474 1C8.84414 1 7.80382 1.20693 6.83319 1.60898C5.86256 2.01103 4.98062 2.60032 4.23774 3.34321C2.73741 4.84354 1.89453 6.87842 1.89453 9.00021C1.89453 11.122 2.73741 13.1569 4.23774 14.6572C5.73807 16.1575 7.77295 17.0004 9.89474 17.0004C12.0165 17.0004 14.0514 16.1575 15.5517 14.6572Z"
                        stroke="#1638B1"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  }
                />
              </Col>
             
              <Col md={0} className="mt-8 mb-20">
                <img
                  className="cursor-pointer"
                  src={FIlterIcon}
                  onClick={() => setOpenModal(true)}
                />
              </Col>
            </Row>
            <Col style={{display:"flex",justifyContent:"space-between"}}>
              {displayData.length > 0 && (
              // <Row justify="space-between" align="middle" className="mb-20">
                <Col style={{marginTop:"10px",marginLeft:"0px"}}>
                  <Checkbox
                    indeterminate={isSomeCurrentPageSelected}
                    checked={isAllCurrentPageSelected}
                    onChange={handleSelectAllToggle}
                    disabled={displayDataIds.length === 0}
                    style={{fontSize:"15px",color:"#122DBE",textDecoration:"underline"}}
                  >
                    Select All 
                  </Checkbox>
                </Col>
              // </Row>
            )}
             {displayData.length > 0 && (
                <Col span={2} style={{ display: "flex", justifyContent: "flex-end" ,marginBottom:"10px",marginRight:"16px"}}>
                  <Button
                    type="primary"
                    className="createBtn"
                    onClick={() => {
                      singleUserCampaignDetails();
                      setIsBulkInviteModalOpen(true);
                    }}
                    disabled={selectedCandidates.length === 0}
                    style={{ height: 36, }}
                  >
                    + Invite ({selectedCandidates.length})
                  </Button>
                </Col>
              )}
               
            </Col>
            
            {/* Select All Controls - Only show when not in favourites tab and there are results */}
         
            
            <Col
              span={24}
              className="cards-parent"
              style={{
                // height: "100%",
                // display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              id="scrolling"
            >
              {/* <FullScreenLoader isLoading={loading} /> */}
              <Spin spinning={loading || loadingFavourites}>
                {displayData.length > 0 ? (
                  displayData.map((item, index) => {
                    const candidate = isFavouritesTab ? item.candidateId : item;
                    const candidateId = getCandidateId(candidate);
                    return (
                      <ProfileCards
                        key={index}
                        candidateId={candidateId}
                        data={candidate}
                        filters={filters}
                        search={search}
                        page={page}
                        campaignList={campaignList}
                        setCampaignSearch={setCampaignSearch}
                        campaignSearch={campaignSearch}
                        GetGlobalSearch={GetGlobalSearch}
                        isSelected={selectedCandidates.includes(candidateId)}
                        onSelectToggle={handleCandidateSelectToggle}
                        isFavouritesTab={isFavouritesTab}
                      />
                    );
                  })
                ) : (
                  <div
                    style={{ textAlign: "center", padding: "40px", color: "#666" }}
                  >
                    {isFavouritesTab
                      ? search && search.trim() !== ""
                        ? "No favourites found matching your search"
                        : "No favourites found"
                      : "No results found"}
                  </div>
                )}
              </Spin>
            </Col>
            <Row align={"middle"} justify={"end"}>
              <Col className="pr-20">
                <CustomPagination
                  currentPage={page}
                  setCurrentPage={handlePageChange}
                  total={totalCount}
                  itemPerPage={10}
                />
              </Col>
            </Row>
          </Col>
          {/* filter section */}
          {!isFavouritesTab && (
          <Col xs={0} md={7} className="filter-section">
            <Row className="w-100" justify={"space-between"} align={"middle"}>
              <Col>
                <h4 className="h4">All Filters {`(${filterLength})`}</h4>
              </Col>
              {filterLength > 0 && (
                <Col>
                  <span
                    className="cursor-pointer"
                    onClick={handleClearAllFilters}
                    style={{
                      color: "#122DBE",
                      textDecoration: "underline",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    Clear All
                  </span>
                </Col>
              )}
            </Row>

            <p className="body-text-b2 mb-20">Select filters to apply</p>
            <Col span={24} className="filter-body">
              {filterData.map((item, index) => (
                <SearchListItem
                  key={index}
                  item={item}
                  filters={filters}
                  setFilters={setFilters}
                />
              ))}
            </Col>
          </Col>
          )}
        </Row>
        <Drawer
          placement="right"
          open={openModal}
          closable={false}
          contentWrapperStyle={{
            top: "90px",
            width: "100vw",
          }}
          className="globalSearchMobileDrawer"
        >
          <Row className="w-100" align={"middle"} justify={"space-between"}>
            <Col>
              <h4 className="h4">All Filters {`(${filterLength})`}</h4>
            </Col>
            <Col style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              {filterLength > 0 && (
                <span
                  className="cursor-pointer"
                  onClick={handleClearAllFilters}
                  style={{
                    color: "#122DBE",
                    textDecoration: "underline",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Clear All
                </span>
              )}
              <img
                src={CloseIcon}
                alt="close"
                className="cursor-pointer"
                onClick={() => setOpenModal(false)}
              />
            </Col>
          </Row>

          <p className="body-text-b2 mb-20">Select filters to apply</p>
          <Col span={24} className="filter-body">
            {filterData.map((item, index) => (
              <SearchListItem
                key={index}
                item={item}
                filters={filters}
                setFilters={setFilters}
              />
            ))}
          </Col>
        </Drawer>
        
        <Modal
          title={`Invite ${selectedCandidates.length} selected candidate(s) to:`}
          open={isBulkInviteModalOpen}
          onCancel={() => setIsBulkInviteModalOpen(false)}
          centered
          getContainer={document.body}
          destroyOnClose
          footer={[
            <Row>
              <Col span={24} align={"right"} className="justify_items_end">
                <Button
                  key="submit"
                  className="black_color_button"
                  onClick={handleBulkInvite}
                  loading={bulkInviteLoading}
                  // disabled={Object.values(inviteIds)?.join(",")?.length < 2}
                >
                  Invite
                </Button>
                <Button
                  key="submit"
                  className="cancel_btn"
                  onClick={() => setIsBulkInviteModalOpen(false)}
                >
                  Cancel
                </Button>
                ,
              </Col>
            </Row>,
          ]}
        >
          <Input
            className="search-input mb-20"
            size="large"
            placeholder="Search for a campaign or advert"
            value={campaignSearch}
            onChange={(e) => setCampaignSearch(e.target.value)}
            // prefix={/*...your svg icon code...*/ }
          />
          <div
            className="campaignListContainer"
            style={{ maxHeight: "40vh", overflowY: "auto" }}
          >
            {bulkCampaignList?.map((item, index) => (
              <InviteCampaignDropdown
                setInviteIds={setBulkInviteIds}
                inviteIds={bulkInviteIds}
                item={item}
                key={index}
              />
            ))}
          </div>
        </Modal>
      </SubLayout>
    </div>
  );
}

export default Search;